import React from 'react';
import Layout from '../components/Layout';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ArrowUpRight, Lightbulb, Target, Users, Zap } from 'lucide-react';

// Philosophy/Values data
const values = [
  {
    icon: Lightbulb,
    title: 'Practical Innovation',
    description: 'We build technology that solves real problems, not solutions looking for problems.'
  },
  {
    icon: Target,
    title: 'User-Centric Design',
    description: 'Every product we create starts with understanding what people actually need.'
  },
  {
    icon: Users,
    title: 'Consumer First',
    description: 'We focus on building products that save people time and money in their daily lives.'
  },
  {
    icon: Zap,
    title: 'Rapid Execution',
    description: 'We move fast, test often, and iterate based on real-world feedback.'
  }
];

// Page Header
const PageHeader = () => {
  const [headerRef, isVisible] = useScrollAnimation();

  return (
    <div 
      ref={headerRef}
      className="text-center py-24 md:py-32 px-6 md:px-12"
    >
      <span 
        className={`inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#6366F1] mb-6 transition-all duration-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        About Us
      </span>
      <h1 
        className={`text-5xl md:text-7xl font-semibold tracking-tight text-[#1F2933] leading-[1.1] mb-8 transition-all duration-600 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        data-testid="about-page-title"
      >
        Our Story
      </h1>
      <p 
        className={`text-lg md:text-xl text-[#4B5563] max-w-2xl mx-auto transition-all duration-600 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        data-testid="about-page-description"
      >
        Building the future of practical technology from Atlanta, Georgia.
      </p>
    </div>
  );
};

// Founder Section
const FounderSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation();

  return (
    <section 
      ref={sectionRef}
      className="px-6 md:px-12 pb-24 md:pb-32"
      data-testid="founder-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Image Side */}
          <div 
            className={`relative transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br from-[#1F2933] to-[#374151] relative">
              {/* Founder visual representation */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-40 h-40 rounded-full bg-white/10 flex items-center justify-center mb-6 mx-auto border border-white/20">
                    <span className="text-6xl font-bold text-white">JW</span>
                  </div>
                  <p className="text-white/60 text-sm tracking-wider uppercase">Founder</p>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-8 left-8 w-24 h-24 rounded-full border border-white/10" />
              <div className="absolute bottom-12 right-12 w-16 h-16 rounded-xl border border-[#6366F1]/30" />
              <div className="absolute top-1/3 right-8 w-3 h-3 rounded-full bg-[#6366F1]" />
              <div className="absolute bottom-1/3 left-12 w-2 h-2 rounded-full bg-white/40" />
              
              {/* Grid overlay */}
              <div 
                className="absolute inset-0 opacity-[0.02]"
                style={{
                  backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
                  backgroundSize: '50px 50px'
                }}
              />
            </div>
            
            {/* Location badge */}
            <div className="absolute -bottom-4 -right-4 md:bottom-8 md:-right-8 bg-white rounded-2xl p-4 shadow-lg border border-[#E5E7EB]">
              <p className="text-sm text-[#9CA3AF]">Based in</p>
              <p className="text-lg font-semibold text-[#1F2933]">Atlanta, GA</p>
            </div>
          </div>

          {/* Content Side */}
          <div 
            className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#6366F1] mb-4">
              Meet the Founder
            </span>
            
            <h2 
              className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1F2933] mb-6"
              data-testid="founder-name"
            >
              Joel Williams
            </h2>
            
            <p className="text-lg text-[#6366F1] font-medium mb-6">
              Founder & Managing Partner
            </p>
            
            <div className="space-y-4 text-[#4B5563] leading-relaxed mb-8">
              <p>
                Joel Williams founded MJV Holdings with a clear mission: build practical technology that solves real problems and makes people's lives easier.
              </p>
              <p>
                With a background in consumer technology and a passion for entrepreneurship, Joel has dedicated his career to creating products that save people time and money. Under his leadership, MJV Holdings has grown into a venture studio with a diverse portfolio of innovative companies.
              </p>
              <p>
                Based in Atlanta, Georgia, Joel continues to lead the studio's efforts in identifying opportunities where technology can make a meaningful difference in everyday life.
              </p>
            </div>

            <a 
              href="mailto:hello@mjvholdings.com"
              className="inline-flex items-center gap-2 text-[#6366F1] font-medium hover:text-[#4F46E5] transition-colors group"
              data-testid="founder-contact-link"
            >
              <span>Get in touch</span>
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Philosophy Section
const PhilosophySection = () => {
  const [sectionRef, isVisible] = useScrollAnimation();

  return (
    <section 
      ref={sectionRef}
      className="px-6 md:px-12 py-24 md:py-32 bg-white"
      data-testid="philosophy-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span 
            className={`inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#6366F1] mb-4 transition-all duration-600 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Our Philosophy
          </span>
          <h2 
            className={`text-3xl md:text-5xl font-semibold tracking-tight text-[#1F2933] mb-6 transition-all duration-600 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            How We Build
          </h2>
          <p 
            className={`text-lg text-[#4B5563] max-w-2xl mx-auto transition-all duration-600 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Every company in our portfolio is guided by these core principles.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => {
            const IconComponent = value.icon;
            return (
              <div
                key={index}
                className={`group p-8 rounded-2xl bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#6366F1]/20 hover:shadow-lg transition-all duration-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ 
                  transitionDelay: `${(index + 3) * 100}ms`,
                  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, border-color 0.3s, box-shadow 0.3s'
                }}
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#6366F1]/10 flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110">
                  <IconComponent size={24} className="text-[#6366F1]" />
                </div>
                
                <h3 className="text-xl font-semibold text-[#1F2933] mb-3">
                  {value.title}
                </h3>
                
                <p className="text-[#4B5563] leading-relaxed">
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Mission Statement Section
const MissionSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation();

  return (
    <section 
      ref={sectionRef}
      className="px-6 md:px-12 py-24 md:py-32"
      data-testid="mission-section"
    >
      <div className="max-w-4xl mx-auto text-center">
        <div 
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <div className="w-16 h-16 rounded-2xl bg-[#6366F1] flex items-center justify-center mx-auto mb-8">
            <Target size={32} className="text-white" />
          </div>
          
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#1F2933] mb-8">
            Our Mission
          </h2>
          
          <p className="text-xl md:text-2xl text-[#4B5563] leading-relaxed mb-8">
            "To build consumer platforms, AI tools, and digital products that save people time and money — technology that solves real problems."
          </p>
          
          <div className="flex items-center justify-center gap-2 text-[#9CA3AF]">
            <span className="w-12 h-px bg-[#E5E7EB]" />
            <span className="text-sm">MJV Holdings</span>
            <span className="w-12 h-px bg-[#E5E7EB]" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Main About Page
const AboutPage = () => {
  return (
    <Layout>
      <PageHeader />
      <FounderSection />
      <PhilosophySection />
      <MissionSection />
    </Layout>
  );
};

export default AboutPage;
